import Vue, { PluginFunction } from 'vue'
import Jed from 'jed'

// TODO: вынести типы в папку types
export interface Messages {
	[locale: string]: object
}

type TranslateArgs = [string, InterpolationValue?]
type NTranslateArgs = [string, string, string | number, InterpolationValue?]
type PTranslateArgs = [string, string, InterpolationValue?]
type NPTranslateArgs = [string, string, string, string | number, InterpolationValue?]

type TemplateFn = (str: string, ...args: InterpolationValue[]) => string

export interface Vue2JedOptions {
	messages: Messages
	locale: () => string | string
	templateFn?: TemplateFn
}

interface InstanceOptions {
	instance: Vue2Jed
}

type InstallerOptions = Vue2JedOptions | InstanceOptions
type InstallFunction = PluginFunction<InstallerOptions>

type InterpolationValue = string | number | object | (string | number | object)[]

declare module 'vue/types/vue' {
	export interface Vue {
		$i18n: Vue2Jed
		$t(...args: TranslateArgs): string
		$tp(...args: NTranslateArgs): string
		$tc(...args: PTranslateArgs): string
		$tcp(...args: NPTranslateArgs): string
	}
}

type StringFunction = () => string

/**
 * Jed docs: http://messageformat.github.io/Jed/
 */
export class Vue2Jed {
	static install: InstallFunction

	locale!: string
	templateFn!: TemplateFn
	locales!: string[]

	private subscribers: Vue[] = []
	private jed!: Jed

	constructor({ messages, locale, templateFn }: Vue2JedOptions) {
		this.subscribers = []

		const vm = new Vue({
			data: { locale: null },
			watch: {
				locale: () => this.notify(),
			},
		})

		Object.defineProperties(this, {
			locale: {
				get: () => vm.locale,
				set: (newLocale: string | StringFunction) => {
					const lang = newLocale instanceof Function ? newLocale() : newLocale
					this.jed = new Jed(messages[lang] || {})
					vm.$set(vm, 'locale', lang)
				},
			},
			locales: { get: () => Object.keys(messages) },
			templateFn: {
				get: () => templateFn || this.sprintf,
				set: (fn) => {
					if (fn != null && !(fn instanceof Function)) {
						throw new Error('"templateFn" should be a Function!')
					}
					templateFn = fn
				},
			},
		})

		this.locale = locale instanceof Function ? locale() : locale
	}

	gettext(key: string, values?: InterpolationValue): string {
		const fn = (key: string) => this.jed.gettext(key)
		return this._translate(fn, key, values)
	}

	pgettext(context: string, key: string, values?: InterpolationValue): string {
		const fn = (key: string) => this.jed.pgettext(context, key)
		return this._translate(fn, key, values)
	}

	ngettext(singularKey: string, pluralKey: string, value: string | number, values?: InterpolationValue): string {
		const fn = (singularKey: string) => this.jed.ngettext(singularKey, pluralKey, value)
		return this._translate(fn, singularKey, values || value)
	}

	npgettext(context: string, singularKey: string, pluralKey: string, value: string | number, values?: InterpolationValue): string {
		const fn = (singularKey: string) => this.jed.npgettext(context, singularKey, pluralKey, value)
		return this._translate(fn, singularKey, values || value)
	}

	/**
	 * For more info see: http://www.diveintojavascript.com/projects/javascript-sprintf
	 */
	sprintf(value: string, ...args: string[]): string {
		return this.jed.sprintf(value, ...args)
	}

	/**
	 * @param {Vue} vm
	 */
	subscribe(vm: Vue) {
		this.subscribers.push(vm)
	}

	/**
	 * @param {Vue} vm
	 */
	unsubscribe(vm: Vue) {
		if (!this.subscribers.length) { return }
		const index = this.subscribers.indexOf(vm)
		if (index > -1) {
			this.subscribers.splice(index, 1)
		}
	}

	notify() {
		this.subscribers.forEach((vm) => vm.$forceUpdate())
	}

	translate(key: string, values: InterpolationValue): string {
		const fn = (s: string) => this.gettext(s)
		return this._translate(fn, key, values)
	}

	// Translation extention methods

	private _translate(
		fn: (key: string) => string,
		key: string,
		values: InterpolationValue = [],
	): string {
		if (typeof key !== 'string') {
			throw new Error(`The 'key' argument must be 'string'!`)
		}
		const translation = fn(key)
		try {
			return this.templateFn(translation, values)
		} catch (e) {
			console.error(`Error while string interpolation: "${key}"`)
			return key
		}
	}
}

const install: InstallFunction = (VueCtor, options) => {
	if (Vue.prototype.$i18n instanceof Vue2Jed) { return }

	const i18n: Vue2Jed = (options as InstanceOptions).instance || new Vue2Jed(options as Vue2JedOptions)

	Vue.mixin({
		beforeCreate() {
			i18n.subscribe(this)
		},
		beforeDestroy(this: Vue) {
			i18n.unsubscribe(this)
		},
	})

	Object.defineProperties(Vue.prototype, {
		$i18n: { get: () => i18n },
		$t: { get: () => i18n.gettext.bind(i18n) },
		$tp: { get: () => i18n.ngettext.bind(i18n) },
		$tc: { get: () => i18n.pgettext.bind(i18n) },
		$tcp: { get: () => i18n.npgettext.bind(i18n) },
	})
}

Vue2Jed.install = install

export default Vue2Jed
